import { ChevronRight, CogIcon, MapPinIcon } from 'lucide-react'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import useSWR, { mutate as globalMutate } from 'swr'

import { PageHeader, ServiceMark } from '@/components/ds'
import { EshopTabs } from '@/components/eshop/EshopTabs'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { BalikovnaDeliverySettingsForm } from '@/forms/BalikovnaDeliverySettingsForm/BalikovnaDeliverySettingsForm'
import { DeliveryMethodForm } from '@/forms/DeliveryMethodForm/DeliveryMethodForm'
import { ZasilkovnaDeliverySettingsForm } from '@/forms/ZasilkovnaDeliverySettingsForm/ZasilkovnaDeliverySettingsForm'
import { Collection } from '@/interfaces/Collection'
import { DeliveryMethod } from '@/interfaces/eshop'
import { Settings } from '@/interfaces/Settings'

import { EnabledBadge, EshopPrices, SkeletonRows } from './components/EshopParts'

const stop = (e: MouseEvent) => e.stopPropagation()

export default function EshopDeliveryMethodsPage() {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'eshop.deliveryMethods',
  })
  const { t: te } = useTranslation(undefined, {
    keyPrefix: 'eshop',
  })
  const { t: tp } = useTranslation(undefined, {
    keyPrefix: 'eshop.pickupPoints',
  })

  const {
    data: methods,
    isLoading,
    mutate,
  } = useSWR<Collection<DeliveryMethod>>('/api/v2/delivery-methods', apiFetcher)

  const { data: settings } = useSWR<Settings>('/api/v2/settings', apiFetcher)

  const currencies = [
    settings?.primaryCurrency ?? 'CZK',
    ...(settings?.alternativeCurrencies ?? []),
  ]

  const [editingMethod, setEditingMethod] = useState<DeliveryMethod | null>(
    null,
  )
  const [dialogOpen, setDialogOpen] = useState(false)
  const [configuringMethod, setConfiguringMethod] =
    useState<DeliveryMethod | null>(null)
  const [providerDialogOpen, setProviderDialogOpen] = useState(false)

  const openEditDialog = (method: DeliveryMethod) => {
    setEditingMethod(method)
    setDialogOpen(true)
  }

  const openProviderDialog = (method: DeliveryMethod) => {
    setConfiguringMethod(method)
    setProviderDialogOpen(true)
  }

  useSetPageHeader({
    breadcrumbs: [
      { href: '/eshop/delivery-methods', children: te('title') },
      { href: '/eshop/delivery-methods', children: t('title') },
    ],
  })

  return (
    <>
      <title>{te('title') + ', ' + t('title')}</title>

      <div className="mx-auto w-full max-w-[1280px]">
        <EshopTabs />
        <PageHeader
          title={t('title')}
          description={t('description')}
          actions={
            <Button variant="outline" asChild>
              <Link to="/eshop/pickup-points">
                <MapPinIcon />
                {tp('title')}
              </Link>
            </Button>
          }
        />

        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Doprava</TableHead>
                <TableHead className="hidden md:table-cell">{t('fields.allowedPaymentMethods')}</TableHead>
                <TableHead className="text-right md:w-44">Cena</TableHead>
                <TableHead className="hidden w-32 md:table-cell">Stav</TableHead>
                <TableHead className="w-px md:w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <SkeletonRows rows={5} cols={5} mobileCols={[0, 2, 4]} />}
              {methods?.data?.map((method) => {
                const payments = method.allowedPaymentMethods.map((payment) => te(`paymentMethods.types.${payment}`))
                return (
                  <TableRow key={method.type} onClick={() => openEditDialog(method)} className="group cursor-pointer">
                    <TableCell className="w-full max-w-0 md:w-auto md:max-w-none">
                      <div className="flex items-center gap-3">
                      <ServiceMark service={method.type} />
                      <div className="min-w-0">
                      <p className="truncate font-medium md:overflow-visible">{t(`types.${method.type}`)}</p>
                      {method.type === 'zasilkovna' && method.zasilkovnaPickupSelector && (
                        <p className="truncate text-xs text-muted-foreground md:overflow-visible">
                          {t(`zasilkovnaSettings.fields.selector.options.${method.zasilkovnaPickupSelector}`)}
                        </p>
                      )}
                      {method.type === 'balikovna' && method.balikovnaPickupSelector && (
                        <p className="truncate text-xs text-muted-foreground md:overflow-visible">
                          {t(`balikovnaSettings.fields.selector.options.${method.balikovnaPickupSelector}`)}
                        </p>
                      )}
                      <p className="truncate text-xs text-muted-foreground md:hidden">
                        {!method.isEnabled && 'Vypnuto, '}
                        {payments.length ? payments.join(', ') : 'Platby nenastaveny'}
                      </p>
                      </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden max-w-0 md:table-cell">
                      <p className={payments.length ? 'truncate' : 'text-muted-foreground'}>
                        {payments.length ? payments.join(', ') : 'Nenastaveno'}
                      </p>
                    </TableCell>
                    <TableCell className="text-right">
                      <EshopPrices prices={method.prices} empty="Nenastaveno" />
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <EnabledBadge enabled={method.isEnabled} />
                    </TableCell>
                    <TableCell onClick={stop}>
                      <div className="flex items-center justify-end gap-0.5">
                        {method.type === 'personalPickup' && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon-sm" asChild>
                                <Link to="/eshop/pickup-points" aria-label={tp('title')}>
                                  <MapPinIcon />
                                </Link>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>{tp('title')}</TooltipContent>
                          </Tooltip>
                        )}
                        {(method.type === 'zasilkovna' || method.type === 'balikovna') && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label={method.type === 'zasilkovna' ? t('zasilkovnaSettings.title') : t('balikovnaSettings.title')}
                                onClick={() => openProviderDialog(method)}
                              >
                                <CogIcon />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {method.type === 'zasilkovna' ? t('zasilkovnaSettings.title') : t('balikovnaSettings.title')}
                            </TooltipContent>
                          </Tooltip>
                        )}
                        <button
                          type="button"
                          aria-label="Upravit"
                          onClick={() => openEditDialog(method)}
                          className="hidden size-7 items-center justify-center rounded-md text-muted-foreground opacity-0 md:inline-flex transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                        >
                          <ChevronRight className="size-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <DeliveryMethodForm
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        method={editingMethod}
        currencies={currencies}
        onSuccess={() => {
          mutate()
          // Disabling personal pickup auto-disables the pay-on-pickup
          // payment method on the backend, so refresh that list too.
          globalMutate('/api/v2/payment-methods')
        }}
      />

      <ZasilkovnaDeliverySettingsForm
        open={providerDialogOpen && configuringMethod?.type === 'zasilkovna'}
        onOpenChange={setProviderDialogOpen}
        method={configuringMethod}
        onSuccess={() => {
          mutate()
        }}
      />

      <BalikovnaDeliverySettingsForm
        open={providerDialogOpen && configuringMethod?.type === 'balikovna'}
        onOpenChange={setProviderDialogOpen}
        method={configuringMethod}
        onSuccess={() => {
          mutate()
        }}
      />
    </>
  )
}
